import React, { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { accountIdentity } from '../../components/accountIdentity';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateAccountGroup,
  useDeleteAccountGroup,
  useUpdateAccountGroup,
  useUpdateAccountGroupMembers,
} from '../../hooks/useApi';
import { AccountGroup, AccountGroupTier, ExternalAccount } from '../../types';
import { GROUP_COLORS, eligibilityLabel, groupDotClass } from './AccountGroupChip';
import { Badge } from './Badge';
import { cx, FOCUS_RING_SURFACE } from './styles';

/**
 * Create, rename, recolour and delete groups, set each one's standing Flash
 * Buy rule, and pick its members — the owner's whole group admin in one place.
 *
 * Membership edits save on every tick rather than on a Save button, because a
 * tick can have a side effect (the added account inherits the group's rule),
 * and a change that has already happened to Flash Buy must not sit behind an
 * unsaved form. The rule selects behave the same way, with a toast that says
 * how many accounts it landed on.
 *
 * Layout: the left rail is the list of groups with one "+ New group" action;
 * the right pane is the selected group — name and colour, its standing Flash
 * Buy rule, then its members. A new group takes the next colour no group is
 * using, so creating one is a name and Enter; the swatches are there for
 * anyone who cares.
 */

const SELECT_CLASS = cx(
  'rounded-lg border border-white/[0.10] bg-white/[0.04] px-2.5 py-1.5 text-xs font-medium text-white/80',
  'hover:border-white/20 focus:outline-none disabled:opacity-50',
  FOCUS_RING_SURFACE,
);

const INPUT_CLASS = cx(
  'w-full rounded-lg border border-white/[0.10] bg-white/[0.04] px-3 py-2 text-sm text-white',
  'placeholder-white/30 focus:border-white/20 focus:outline-none',
  FOCUS_RING_SURFACE,
);

type FlashBuyRule = '' | 'on' | 'off';

const ruleFromGroup = (g: AccountGroup): FlashBuyRule =>
  g.flash_buy_enabled === true ? 'on' : g.flash_buy_enabled === false ? 'off' : '';

const isEligible = (a: ExternalAccount) => a.platform === 'DICE' && a.is_active && !a.is_banned;

/** The first colour no existing group wears; cycles once every colour is taken. */
const nextColour = (groups: AccountGroup[]): string => {
  const palette = GROUP_COLORS.filter((c) => c.value !== '');
  const used = new Set(groups.map((g) => g.color || ''));
  const free = palette.find((c) => !used.has(c.value));
  return (free ?? palette[groups.length % palette.length]).value;
};

/** A row of colour dots. Radios, so the keyboard walks them like any group. */
const ColourSwatches: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
}> = ({ value, onChange, disabled, label }) => (
  <div role="radiogroup" aria-label={label} className="flex flex-wrap items-center gap-1.5">
    {GROUP_COLORS.map((c) => {
      const selected = (value || '') === c.value;
      return (
        <button
          key={c.value || 'grey'}
          type="button"
          role="radio"
          aria-checked={selected}
          aria-label={c.label}
          title={c.label}
          disabled={disabled}
          onClick={() => onChange(c.value)}
          className={cx(
            'flex h-6 w-6 items-center justify-center rounded-full transition-all disabled:opacity-40',
            selected ? 'ring-2 ring-white/70 ring-offset-2 ring-offset-[#0f0f0f]' : 'hover:scale-110',
            FOCUS_RING_SURFACE,
          )}
        >
          <span className={cx('h-3.5 w-3.5 rounded-full', c.dot)} />
        </button>
      );
    })}
  </div>
);

export interface ManageGroupsModalProps {
  open: boolean;
  onClose: () => void;
  accounts: ExternalAccount[];
  groups: AccountGroup[];
  /** Open straight onto this group. */
  initialGroupId?: number | null;
  /** Pre-tick this account in a new group (the "+ New group" path from a card). */
  initialAccountId?: number | null;
}

export const ManageGroupsModal: React.FC<ManageGroupsModalProps> = ({
  open,
  onClose,
  accounts,
  groups,
  initialGroupId = null,
  initialAccountId = null,
}) => {
  const { showSuccess, showError } = useToast();
  const createGroup = useCreateAccountGroup();
  const updateGroup = useUpdateAccountGroup();
  const deleteGroup = useDeleteAccountGroup();
  const updateMembers = useUpdateAccountGroupMembers();

  const [selectedId, setSelectedId] = useState<number | null>(initialGroupId);
  // The create form only shows while `creating`; it opens itself when the
  // modal was reached via "New group…" from an account card.
  const [creating, setCreating] = useState(initialAccountId != null);
  const [newName, setNewName] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Account to seed into the next group created from this modal.
  const [seedAccountId, setSeedAccountId] = useState<number | null>(initialAccountId);

  useEffect(() => {
    if (!open) return;
    setSelectedId(initialGroupId);
    setSeedAccountId(initialAccountId);
    setCreating(initialAccountId != null);
    setNewName('');
    setMemberQuery('');
    setConfirmDelete(false);
  }, [open, initialGroupId, initialAccountId]);

  const selected = useMemo(
    () => groups.find((g) => g.id === selectedId) ?? null,
    [groups, selectedId],
  );

  useEffect(() => {
    setNameDraft(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  const memberSet = useMemo(() => new Set(selected?.member_ids ?? []), [selected]);

  const visibleAccounts = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    const rows = q
      ? accounts.filter((a) => accountIdentity(a).toLowerCase().includes(q) || a.platform.toLowerCase().includes(q))
      : accounts;
    // Members first, then eligible DICE, then the rest — so what the group
    // does is at the top and what it will skip is at the bottom.
    return [...rows].sort((a, b) => {
      const am = memberSet.has(a.id) ? 0 : 1;
      const bm = memberSet.has(b.id) ? 0 : 1;
      if (am !== bm) return am - bm;
      const ae = isEligible(a) ? 0 : 1;
      const be = isEligible(b) ? 0 : 1;
      if (ae !== be) return ae - be;
      return accountIdentity(a).localeCompare(accountIdentity(b));
    });
  }, [accounts, memberQuery, memberSet]);

  if (!open) return null;

  const busy =
    createGroup.isPending || updateGroup.isPending || deleteGroup.isPending || updateMembers.isPending;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const created = await createGroup.mutateAsync({
        name,
        color: nextColour(groups),
        account_ids: seedAccountId != null ? [seedAccountId] : [],
      });
      setNewName('');
      setCreating(false);
      setSeedAccountId(null);
      setSelectedId(created.id);
      showSuccess(`Created "${created.name}"`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not create the group');
    }
  };

  const commitName = async () => {
    if (!selected) return;
    const name = nameDraft.trim();
    if (!name || name === selected.name) {
      setNameDraft(selected.name);
      return;
    }
    try {
      await updateGroup.mutateAsync({ id: selected.id, payload: { name } });
    } catch (err) {
      setNameDraft(selected.name);
      showError(err instanceof Error ? err.message : 'Could not rename the group');
    }
  };

  const setColor = async (color: string) => {
    if (!selected) return;
    try {
      await updateGroup.mutateAsync({ id: selected.id, payload: { color } });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not update the colour');
    }
  };

  const describeApplied = (applied?: { applied: number; tier_updated: number; eligible: number; members: number }) => {
    if (!applied) return 'Rule saved.';
    const bits: string[] = [];
    if (applied.applied > 0) bits.push(`${applied.applied} switched`);
    if (applied.tier_updated > 0) bits.push(`${applied.tier_updated} tier${applied.tier_updated !== 1 ? 's' : ''} set`);
    const head = bits.length > 0 ? bits.join(', ') : 'Nothing to change';
    return `${head} · ${eligibilityLabel(applied.eligible, applied.members)}. Accounts added later get the same rule.`;
  };

  const setFlashBuyRule = async (value: FlashBuyRule) => {
    if (!selected) return;
    try {
      const res = await updateGroup.mutateAsync({
        id: selected.id,
        payload: { flash_buy_enabled: value === '' ? null : value === 'on' },
      });
      showSuccess(value === '' ? 'Flash Buy rule cleared. Members keep their current state.' : describeApplied(res.applied));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not update the rule');
    }
  };

  const setTierRule = async (value: AccountGroupTier) => {
    if (!selected) return;
    try {
      const res = await updateGroup.mutateAsync({
        id: selected.id,
        payload: { tier_match: value === '' ? null : value },
      });
      showSuccess(value === '' ? 'Tier rule cleared. Members keep their current tier.' : describeApplied(res.applied));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not update the tier rule');
    }
  };

  const toggleMember = async (account: ExternalAccount) => {
    if (!selected) return;
    const isMember = memberSet.has(account.id);
    try {
      const res = await updateMembers.mutateAsync(
        isMember ? { id: selected.id, remove: [account.id] } : { id: selected.id, add: [account.id] },
      );
      if (!isMember && (res.inherited.applied > 0 || res.inherited.tier_updated > 0)) {
        showSuccess(`Added — and it picked up the group's Flash Buy rule.`);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not update members');
    }
  };

  const setAllVisible = async (add: boolean) => {
    if (!selected) return;
    const ids = visibleAccounts
      .filter((a) => (add ? !memberSet.has(a.id) : memberSet.has(a.id)))
      .map((a) => a.id);
    if (ids.length === 0) return;
    try {
      const res = await updateMembers.mutateAsync(add ? { id: selected.id, add: ids } : { id: selected.id, remove: ids });
      if (add && (res.inherited.applied > 0 || res.inherited.tier_updated > 0)) {
        showSuccess(`Added ${ids.length} — ${res.inherited.applied} picked up the group's Flash Buy rule.`);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not update members');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteGroup.mutateAsync(selected.id);
      showSuccess(`Deleted "${selected.name}". Its accounts were not changed.`);
      setSelectedId(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not delete the group');
    } finally {
      setConfirmDelete(false);
    }
  };

  const seedAccount = seedAccountId != null ? accounts.find((a) => a.id === seedAccountId) : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-groups-title"
        className="flex h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-[#0f0f0f] sm:h-[640px] sm:max-h-[92vh] sm:rounded-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-white/[0.08] px-5 py-4">
          <div className="min-w-0">
            <h2 id="manage-groups-title" className="text-base font-medium text-white">Account groups</h2>
            <p className="mt-0.5 text-xs text-white/40">
              Tag accounts so Flash Buy and waitlists can target them by name. An account can be in several groups.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cx('rounded-md p-1 text-white/40 hover:text-white/80', FOCUS_RING_SURFACE)}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden sm:grid-cols-[240px_1fr]">
          {/* Left: the groups, and one way to add another */}
          <aside className="flex min-h-0 flex-col border-b border-white/[0.08] sm:border-b-0 sm:border-r">
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {groups.length === 0 && !creating ? (
                <p className="px-2 py-3 text-xs text-white/40">No groups yet.</p>
              ) : (
                <ul className="space-y-0.5">
                  {groups.map((g) => (
                    <li key={g.id}>
                      <button
                        type="button"
                        onClick={() => { setSelectedId(g.id); setCreating(false); }}
                        aria-current={g.id === selectedId && !creating ? 'true' : undefined}
                        className={cx(
                          'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
                          g.id === selectedId && !creating ? 'bg-white/[0.08] text-white' : 'text-white/70 hover:bg-white/[0.04]',
                          FOCUS_RING_SURFACE,
                        )}
                      >
                        <span className={cx('h-2 w-2 flex-shrink-0 rounded-full', groupDotClass(g.color))} />
                        <span className="min-w-0 flex-1 truncate">{g.name}</span>
                        <span className="text-xs tabular-nums text-white/35">{g.member_count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t border-white/[0.06] p-2">
              {creating ? (
                <form onSubmit={handleCreate} className="space-y-2 px-1 py-1">
                  <input
                    type="text"
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
                    placeholder="Group name"
                    maxLength={60}
                    aria-label="New group name"
                    className={INPUT_CLASS}
                  />
                  {seedAccount && (
                    <p className="text-[11px] text-white/40">
                      Starts with <span className="text-white/70">{accountIdentity(seedAccount)}</span>.
                    </p>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => { setCreating(false); setNewName(''); }}
                      className={cx('rounded-lg px-2.5 py-1.5 text-xs text-white/50 hover:text-white/80', FOCUS_RING_SURFACE)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={busy || !newName.trim()}
                      className={cx(
                        'rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-40',
                        FOCUS_RING_SURFACE,
                      )}
                    >
                      Create
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className={cx(
                    'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-white/60 transition-colors hover:bg-white/[0.04] hover:text-white',
                    FOCUS_RING_SURFACE,
                  )}
                >
                  <span className="text-base leading-none text-white/40">+</span>
                  New group
                </button>
              )}
            </div>
          </aside>

          {/* Right: the selected group */}
          <section className="flex min-h-0 flex-col">
            {!selected || creating ? (
              <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-white/40">
                {creating
                  ? 'Name the group, then pick its members here.'
                  : groups.length === 0
                    ? 'Create a group to start tagging accounts.'
                    : 'Pick a group to edit its members and rule.'}
              </div>
            ) : (
              <>
                <div className="space-y-4 border-b border-white/[0.06] p-4">
                  <div className="flex items-center gap-3">
                    <span className={cx('h-3 w-3 flex-shrink-0 rounded-full', groupDotClass(selected.color))} />
                    <input
                      type="text"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onBlur={commitName}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      maxLength={60}
                      aria-label="Group name"
                      className={cx(
                        'min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-base font-medium text-white',
                        'hover:border-white/[0.10] focus:border-white/20 focus:bg-white/[0.04] focus:outline-none',
                        FOCUS_RING_SURFACE,
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      disabled={busy}
                      className={cx(
                        'flex-shrink-0 rounded-lg px-2 py-1 text-xs text-white/40 transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50',
                        FOCUS_RING_SURFACE,
                      )}
                    >
                      Delete
                    </button>
                  </div>

                  <ColourSwatches
                    label="Group colour"
                    value={selected.color || ''}
                    onChange={setColor}
                    disabled={busy}
                  />

                  {/* The standing rule: applied now, and to whoever joins later. */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-white/80">Flash Buy rule</p>
                      <p className="text-[11px] text-white/40">
                        Applied to eligible members now and to any account added later.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-white/50">
                      Flash Buy
                      <select
                        value={ruleFromGroup(selected)}
                        onChange={(e) => setFlashBuyRule(e.target.value as FlashBuyRule)}
                        disabled={busy}
                        className={SELECT_CLASS}
                      >
                        <option value="">No rule</option>
                        <option value="on">On</option>
                        <option value="off">Off</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-white/50">
                      Tier
                      <select
                        value={selected.tier_match}
                        onChange={(e) => setTierRule(e.target.value as AccountGroupTier)}
                        disabled={busy}
                        className={SELECT_CLASS}
                      >
                        <option value="">No rule</option>
                        <option value="match">Match</option>
                        <option value="ga">GA</option>
                        <option value="vip">VIP</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Members */}
                <div className="space-y-2 border-b border-white/[0.06] px-4 py-2.5">
                  <p className="text-xs font-medium text-white/80">
                    Members
                    <span className="ml-1.5 font-normal text-white/40">
                      {selected.member_count === 0
                        ? 'none yet'
                        : `${eligibilityLabel(selected.eligible_flash_buy_count, selected.member_count, selected.skipped)} for Flash Buy`}
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={memberQuery}
                      onChange={(e) => setMemberQuery(e.target.value)}
                      placeholder="Filter accounts…"
                      aria-label="Filter accounts"
                      className={cx(INPUT_CLASS, 'py-1.5 text-xs')}
                    />
                    <button
                      type="button"
                      onClick={() => setAllVisible(true)}
                      disabled={busy || visibleAccounts.every((a) => memberSet.has(a.id))}
                      className={cx('whitespace-nowrap rounded px-1.5 py-1 text-xs text-[#60a5fa] hover:underline disabled:opacity-40', FOCUS_RING_SURFACE)}
                    >
                      Add all shown
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllVisible(false)}
                      disabled={busy || !visibleAccounts.some((a) => memberSet.has(a.id))}
                      className={cx('whitespace-nowrap rounded px-1.5 py-1 text-xs text-white/50 hover:underline disabled:opacity-40', FOCUS_RING_SURFACE)}
                    >
                      Remove all shown
                    </button>
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-2">
                  {accounts.length === 0 ? (
                    <p className="px-2 py-3 text-xs text-white/40">No connected accounts to add.</p>
                  ) : visibleAccounts.length === 0 ? (
                    <p className="px-2 py-3 text-xs text-white/40">No accounts match that filter.</p>
                  ) : (
                    <ul className="space-y-0.5">
                      {visibleAccounts.map((a) => {
                        const checked = memberSet.has(a.id);
                        const eligible = isEligible(a);
                        return (
                          <li key={a.id}>
                            <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-white/[0.04]">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={busy}
                                onChange={() => toggleMember(a)}
                                className={cx('h-4 w-4 flex-shrink-0 cursor-pointer accent-[#3b82f6]', FOCUS_RING_SURFACE)}
                              />
                              <span className="w-10 flex-shrink-0 text-[11px] font-semibold text-white/50">{a.platform}</span>
                              <span className={cx('min-w-0 flex-1 truncate text-sm', eligible ? 'text-white/80' : 'text-white/45')}>
                                {accountIdentity(a)}
                              </span>
                              {!eligible && (
                                <Badge tone={a.is_banned ? 'red' : 'neutral'} size="sm">
                                  {a.is_banned ? 'Banned' : a.platform !== 'DICE' ? 'Not DICE' : 'Inactive'}
                                </Badge>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={selected ? `Delete "${selected.name}"?` : 'Delete group?'}
        message="The accounts in it are not changed — they keep whatever Flash Buy state they have now. Only the tag goes away."
        confirmLabel="Delete group"
        destructive
        busy={deleteGroup.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </div>
  );
};
